import API_BASE_URL from "../RestAPI";

const BASE = `${API_BASE_URL.replace(/\/$/, "")}/customer-support`;

const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
};

const buildQuery = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
        if (val == null || val === "") return;
        if (Array.isArray(val)) {
            val.forEach((v) => {
                if (v != null && v !== "") qs.append(key, String(v));
            });
        } else {
            qs.append(key, String(val));
        }
    });
    const s = qs.toString();
    return s ? `?${s}` : "";
};

const handle = async (res, fallbackMsg) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.message || res.statusText || fallbackMsg);
    }
    return data;
};

export const customerSupportApi = {
    // ── Dashboard ───────────────────────────────────────
    getDashboard: async (params = {}) => {
        // Optional params: { preset, date_from, date_to }.
        const res = await fetch(`${BASE}/dashboard/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to fetch dashboard");
    },

    // ── Tickets (read) ──────────────────────────────────
    getTickets: async (params = {}) => {
        const res = await fetch(`${BASE}/tickets/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to fetch tickets");
    },
    exportTickets: async (params = {}) => {
        // Returns a CSV Blob shaped by the same filter params as getTickets.
        const res = await fetch(`${BASE}/tickets/export/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || res.statusText || "Failed to export tickets");
        }
        return res.blob();
    },
    getTicket: async (id) => {
        const res = await fetch(`${BASE}/tickets/${id}/`, { method: "GET", headers: getAuthHeaders() });
        return handle(res, "Failed to fetch ticket");
    },
    updateTriage: async (id, payload) => {
        // PUT-only FixDesk-side fields: fixdesk_acknowledged, fixdesk_triage_priority, fixdesk_internal_note.
        const res = await fetch(`${BASE}/tickets/${id}/`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to update triage state");
    },
    addComment: async (id, payload) => {
        // Post a reply. payload: { message: "..." }. Always recorded as agent.
        const res = await fetch(`${BASE}/tickets/${id}/comments/`, {
            method: "POST", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to post reply");
    },

    // ── Employees (agent filter dropdown + assignee picker) ──
    getEmployees: async () => {
        const res = await fetch(`${API_BASE_URL.replace(/\/$/, "")}/core/employee/`, {
            method: "GET", headers: getAuthHeaders(),
        });
        const data = await handle(res, "Failed to fetch employees");
        return data.data || [];
    },

    // ── Tenants (org filter dropdown) ───────────────────
    // Response: { data: [tenant…], servers: [{ code, label }…], products: [{ code, label }…] }.
    // `servers` lists the RDS instances FixDesk pulls from — with more than one,
    // tenant labels need an environment suffix to stay unambiguous. `products`
    // are the products (eMedHub, Ayurveda) for the Product filter.
    // Optional params: { server: "uat" } or { product: "ayurveda" } to scope.
    getTenants: async (params = {}) => {
        const res = await fetch(`${BASE}/tenants/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to fetch tenants");
    },
    addTenant: async (payload) => {
        // payload: { db_name, server_code?, org_name?, org_id?, partition_type? }
        const res = await fetch(`${BASE}/tenants/`, {
            method: "POST", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to register tenant");
    },
    updateTenant: async (payload) => {
        // payload: { id, ...changes }
        const res = await fetch(`${BASE}/tenants/`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to update tenant");
    },

    // ── Turnaround targets (cs_sla_policy) ──────────────
    // The numbers used to live in a SLA_HOURS constant down this file AND in
    // the backend's serializers.py, which is exactly how they drifted apart.
    // They are server-owned now; nothing here keeps a second copy.
    getSlaPolicies: async (params = {}) => {
        const res = await fetch(`${BASE}/sla-policy/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to fetch turnaround targets");
    },
    updateSlaPolicy: async (payload) => {
        // payload: { id, first_response_mins?, resolution_mins?, clock_type?, … }
        const res = await fetch(`${BASE}/sla-policy/`, {
            method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to update target");
    },

    // ── Measured turnaround ─────────────────────────────
    // p50 / p75 / p90 per severity. What the targets above should be set FROM.
    getTatReport: async (params = {}) => {
        // Optional params: { days, group_by, ...any ticket-list filter }.
        const res = await fetch(`${BASE}/reports/tat/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to fetch turnaround report");
    },

    // ── Sync (manual trigger; cron runs `manage.py sync_support`) ──
    // Pass `server` to sync one eMedHub RDS instance only; omit for all of them.
    triggerSync: async ({ refresh_registry = true, server = null, product = null } = {}) => {
        const body = { refresh_registry };
        if (server) body.server = server;
        // Optional: sync one product's servers only ("emedhub", "ayurveda").
        if (product) body.product = product;
        const res = await fetch(`${BASE}/sync/`, {
            method: "POST", headers: getAuthHeaders(),
            body: JSON.stringify(body),
        });
        return handle(res, "Failed to trigger sync");
    },
};

// ── Static enumerations the filter UI uses ──
export const STATUS_VALUES   = ["Open", "In Progress", "Resolved", "Closed"];
export const PRIORITY_VALUES = ["Critical", "High", "Medium", "Low"];
export const TYPE_VALUES     = ["Bug", "Issue", "Support", "Requirement", "Query"];
export const SOURCE_VALUES   = ["Portal", "Email", "Phone", "Chat", "API"];

// FixDesk-side triage type — set by the support team, distinct from the
// eMedHub-sourced TYPE_VALUES above.
export const FIXDESK_TYPE_VALUES = [
    "Bug", "Requirement", "Miscommunication", "Enhancement", "Question", "Other", "Support Resolved"
];
export const FIXDESK_TYPE_DOT = {
    "Bug":              "#f87171",
    "Requirement":      "#7c5cff",
    "Miscommunication": "#fb923c",
    "Enhancement":      "#38bdf8",
    "Question":         "#facc15",
    "Other":            "#94a3b8",
    "Support Resolved": "#4ade80"
};
export const SLA_VALUES      = ["ok", "warn", "danger"];

// ── Severity (P0–P3) — FixDesk's own triage tier ──
//
// NOT the same thing as `category` (Billing / Payment, Patient Records, …),
// which is the functional area a ticket belongs to, and NOT the same thing as
// `priority` (Critical / High / …), which the CUSTOMER sets inside eMedHub.
// Severity is what the support team assigns after triage, and it is the only
// one of the three that a turnaround target runs against.
export const SEVERITY_VALUES = ["P0", "P1", "P2", "P3"];

// "Untriaged" is a filter value, not a stored one — the backend maps it to
// rows with no severity set. Worth surfacing: a ticket with no tier has no
// clock running against it.
export const SEVERITY_FILTER_VALUES = [...SEVERITY_VALUES, "Untriaged"];

export const SEVERITY_DOT = {
    "P0":        "#f87171",
    "P1":        "#fb923c",
    "P2":        "#facc15",
    "P3":        "#4ade80",
    "Untriaged": "#666880",
};
export const SEVERITY_CHIP_CLS = {
    "P0": "sev-p0",
    "P1": "sev-p1",
    "P2": "sev-p2",
    "P3": "sev-p3",
};

// Shown under the severity picker in the triage panel. A tier nobody can
// define gets used wrongly within a fortnight, so the definitions travel with
// the control. Kept in step with SEVERITY_HELP in customer_support/sla.py.
export const SEVERITY_HELP = {
    "P0": "Operations stopped — OP registration, billing or IP admission unusable at a site, no workaround. Somebody would call at 2am about it.",
    "P1": "A major function is broken for many users, or data is visibly wrong. Workaround exists but costs real time.",
    "P2": "One screen or feature misbehaves for some users and a workaround is available. Most bugs belong here.",
    "P3": "Cosmetic, minor or enhancement-shaped. Nothing breaks if it ships next month.",
};

export const SEVERITY_SHORT = {
    "P0": "Operations stopped",
    "P1": "Major function broken",
    "P2": "Workaround available",
    "P3": "Minor / cosmetic",
};

export const STATUS_DOT = {
    "Open":          "#3b82f6",
    "In Progress":   "#facc15",
    "Resolved":      "#4ade80",
    "Closed":        "#666880",
    "Support Resolved": "#4ade80"
};
export const STATUS_CHIP_CLS = {
    "Open":          "chip-open",
    "In Progress":   "chip-prog",
    "Resolved":      "chip-res",
    "Closed":        "chip-closed",
    "Support Resolved": "chip-support-resolved",
};
export const PRIORITY_DOT = {
    "Critical": "#f87171",
    "High":     "#fb923c",
    "Medium":   "#facc15",
    "Low":      "#4ade80",
};
export const PRIORITY_CHIP_CLS = {
    "Critical": "pr-crit",
    "High":     "pr-high",
    "Medium":   "pr-med",
    "Low":      "pr-low",
};

// Build an "Xs ago" / "Xh ago" string from an ISO timestamp.
export const relativeTime = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60)   return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

// Time remaining against the ticket's resolution deadline.
//
// This used to hold its own copy of the SLA hours (`SLA_HOURS = { Critical: 4,
// … }`) duplicating a table in the backend's serializers.py, and computed the
// deadline client-side from `priority`. Both halves were wrong: the tables
// drifted, and `priority` is set by the customer, who files nearly everything
// as Critical.
//
// The deadline is now stamped server-side from cs_sla_policy and arrives on the
// ticket as `resolution_due_at`, so this only formats what it is given.
export const slaText = (ticket) => {
    if (!ticket) return "";
    if (ticket.status === "Resolved" || ticket.status === "Closed") return "Done";
    // No deadline means no severity has been set, so no clock is running.
    if (!ticket.resolution_due_at) return "No target";

    const ms = new Date(ticket.resolution_due_at).getTime() - Date.now();
    if (Number.isNaN(ms)) return "—";
    const negative = ms < 0;
    const abs = Math.abs(ms);
    const sign = negative ? "-" : "";
    if (abs < 86400000) {
        const hh = Math.floor(abs / 3600000);
        const mm = Math.floor((abs % 3600000) / 60000);
        return `${sign}${hh}h ${mm.toString().padStart(2, "0")}m`;
    }
    const dd = Math.floor(abs / 86400000);
    const rh = Math.floor((abs % 86400000) / 3600000);
    return `${sign}${dd}d ${rh}h`;
};

// Format a raw minute count the same way the backend's humanize_minutes does,
// for the few places the FE derives a duration itself (a target the user is
// editing, before it has been saved and read back).
export const formatMinutes = (mins) => {
    if (mins == null || Number.isNaN(Number(mins))) return "";
    const m = Math.max(0, Math.round(Number(mins)));
    const days = Math.floor(m / 1440);
    const hours = Math.floor((m % 1440) / 60);
    const minutes = m % 60;
    if (days) return `${days}d ${hours}h`;
    if (hours) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

// Compute initials for the agent avatar pill.
export const initials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_GRADIENTS = [
    "linear-gradient(135deg,#3b82f6,#90b8ff)",
    "linear-gradient(135deg,#ef4444,#f87171)",
    "linear-gradient(135deg,#22c55e,#4ade80)",
    "linear-gradient(135deg,#eab308,#facc15)",
    "linear-gradient(135deg,#7c5cff,#a685ff)",
    "linear-gradient(135deg,#ec4899,#f9a8d4)",
    "linear-gradient(135deg,#04b7b1,#5eead4)",
    "linear-gradient(135deg,#f97316,#fb923c)",
];

export const avatarGradient = (key) => {
    if (!key) return AVATAR_GRADIENTS[0];
    const s = String(key);
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};
